import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Form,
  Col,
  Row,
  Spinner,
  Alert,
} from "react-bootstrap";
import moment from "moment";
import { useSelector } from "react-redux";
import { selectApiUrls } from "../../redux/slices/apiUrlSlice";
import useRealtimeReadOnlyList from "../../realtime/useRealtimeReadOnlyList";

const PanelSizingChangeLog = () => {
  const apiUrls = useSelector(selectApiUrls);
  
  const { data: changeLogs, isLoading, error: queryError, refetch: fetchPanelSizingChangeLog } =
    useRealtimeReadOnlyList({ url: apiUrls.panelSizingChangeLog });
  const error = queryError ? 'Failed to fetch panel sizing change log data' : null;

  const [filteredLog, setFilteredLog] = useState([]);
  const [userFilter, setUserFilter] = useState(null);
  const [jobNameFilter, setJobNameFilter] = useState(null);
  const [fieldFilter, setFieldFilter] = useState(null);
  const [roleFilter, setRoleFilter] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateRange, setDateRange] = useState([null, null]);

  const [uniqueUsers, setUniqueUsers] = useState([]);
  const [uniqueJobNames, setUniqueJobNames] = useState([]);
  const [uniqueFields, setUniqueFields] = useState([]);
  const [uniqueRoles, setUniqueRoles] = useState([]);


  const formatValue = (value) => {
    if (value === null || value === undefined || value === "") {
      return "BLANK";
    }
    if (value === false || value === "false" || value === "Off" || value === "No") {
      return "No";
    }
    if (value === true || value === "true" || value === "On" || value === "Yes") {
      return "Yes";
    }
    return value;
  };

  // Format field names for better display
  const formatFieldName = (field) => {
    if (!field) return "";
    return field
      .replace(/([a-z])([A-Z])/g, "$1 $2") // Add space before uppercase letters
      .replace(/^[a-z]/, (match) => match.toUpperCase()); // Capitalize the first letter
  };

  // Extract unique filter values
  useEffect(() => {
    if (changeLogs.length) {
      setUniqueUsers([...new Set(changeLogs.map((item) => item.CurrentUser))]);
      setUniqueJobNames([...new Set(changeLogs.map((item) => item.JobName))]);
      setUniqueFields([...new Set(changeLogs.map((item) => formatFieldName(item.Field)))]);
      setUniqueRoles([...new Set(changeLogs.map((item) => item.UserRole))]);
    } else {
      setUniqueUsers([]);
      setUniqueJobNames([]);
      setUniqueFields([]);
      setUniqueRoles([]);
    }
  }, [changeLogs]);

  // Apply filters
  useEffect(() => {
    if (!changeLogs.length) { setFilteredLog([]); return; }

    // Sort changeLogs by DateTime descending to have the most recent at the top
    let sortedLog = [...changeLogs].sort(
      (a, b) => new Date(b.DateTime) - new Date(a.DateTime)
    );

    let filtered = sortedLog;

    if (userFilter) {
      filtered = filtered.filter((item) => item.CurrentUser === userFilter);
    }

    if (jobNameFilter) {
      filtered = filtered.filter((item) => item.JobName === jobNameFilter);
    }

    if (fieldFilter) {
      filtered = filtered.filter(
        (item) => formatFieldName(item.Field) === fieldFilter
      );
    }

    if (roleFilter) {
      filtered = filtered.filter((item) => item.UserRole === roleFilter);
    }

    if (searchTerm) {
      filtered = filtered.filter((item) =>
        Object.values(item).some((value) =>
          value?.toString().toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }

    if (dateRange[0] && dateRange[1]) {
      filtered = filtered.filter((item) => {
        const itemDate = moment.utc(item.DateTime);
        const startDate = moment.utc(dateRange[0], "YYYY-MM-DD").startOf("day");
        const endDate = moment.utc(dateRange[1], "YYYY-MM-DD").endOf("day");
        return itemDate.isBetween(startDate, endDate, "minutes", "[]");
      });
    }

    setFilteredLog(filtered);
  }, [
    changeLogs,
    userFilter,
    jobNameFilter,
    fieldFilter,
    roleFilter,
    searchTerm,
    dateRange,
  ]);

  const clearFilters = () => {
    setUserFilter(null);
    setJobNameFilter(null);
    setFieldFilter(null);
    setRoleFilter(null);
    setSearchTerm("");
    setDateRange([null, null]);
  };

  return (
    <div className="panel-sizing-change-log-container">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4>Panel Sizing Change Log</h4>
        <div className="d-flex gap-2">
          <Button 
            variant="outline-secondary" 
            size="sm"
            onClick={clearFilters}
          >
            Clear Filters
          </Button>
          <Button 
            variant="outline-primary" 
            size="sm"
            onClick={fetchPanelSizingChangeLog}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <div className="spinner-border spinner-border-sm me-2" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                Refreshing...
              </>
            ) : (
              <>
                <i className="bi bi-arrow-repeat me-1"></i>
                Refresh
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="controls-section mb-3" style={{ padding: "1rem", backgroundColor: "#f8f9fa", borderRadius: "0.375rem" }}>
        <Form>
          <Row className="g-3">
            {/* First Row of Filters */}
            <Col md={3}>
              <Form.Group controlId="userFilter">
                <Form.Label>User Filter</Form.Label>
                <Form.Select
                  size="sm"
                  onChange={(e) => setUserFilter(e.target.value || null)}
                  value={userFilter || ""}
                >
                  <option value="">All Users</option>
                  {uniqueUsers.map((user, index) => (
                    <option key={index} value={user}>
                      {user}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group controlId="jobNameFilter">
                <Form.Label>Job Name Filter</Form.Label>
                <Form.Select
                  size="sm"
                  onChange={(e) => setJobNameFilter(e.target.value || null)}
                  value={jobNameFilter || ""}
                >
                  <option value="">All Job Names</option>
                  {uniqueJobNames.map((jobName, index) => (
                    <option key={index} value={jobName}>
                      {jobName}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group controlId="fieldFilter">
                <Form.Label>Field Filter</Form.Label>
                <Form.Select
                  size="sm"
                  onChange={(e) => setFieldFilter(e.target.value || null)}
                  value={fieldFilter || ""}
                >
                  <option value="">All Fields</option>
                  {uniqueFields.map((field, index) => (
                    <option key={index} value={field}>
                      {field}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group controlId="roleFilter">
                <Form.Label>Role Filter</Form.Label>
                <Form.Select
                  size="sm"
                  onChange={(e) => setRoleFilter(e.target.value || null)}
                  value={roleFilter || ""}
                >
                  <option value="">All Roles</option>
                  {uniqueRoles.map((role, index) => (
                    <option key={index} value={role}>
                      {role}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
          
          {/* Second Row of Filters */}
          <Row className="g-3 mt-2">
            <Col md={3}>
              <Form.Group controlId="dateRangeStart">
                <Form.Label>Start Date</Form.Label>
                <Form.Control
                  type="date"
                  size="sm"
                  value={dateRange[0] || ""}
                  onChange={(e) => setDateRange([e.target.value || null, dateRange[1]])}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group controlId="dateRangeEnd">
                <Form.Label>End Date</Form.Label>
                <Form.Control
                  type="date"
                  size="sm"
                  value={dateRange[1] || ""}
                  onChange={(e) => setDateRange([dateRange[0], e.target.value || null])}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="searchTerm">
                <Form.Label>Search</Form.Label>
                <Form.Control
                  type="text"
                  size="sm"
                  placeholder="Search all fields..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>
        </Form>
      </div>

      {/* Results Summary */}
      <div className="mb-3">
        <span className="text-muted">
          Showing {filteredLog.length} of {changeLogs.length} entries
        </span>
      </div>

      {/* Error Display */}
      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}

      {/* Loading / Data Display */}
      {isLoading ? (
        <div className="text-center p-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">Loading panel sizing change log...</p>
        </div>
      ) : filteredLog.length === 0 ? (
        <Alert variant="info">
          {changeLogs.length === 0 ? "No panel sizing changes found" : "No changes match the current filters"}
        </Alert>
      ) : (
        <div style={{ maxHeight: "600px", overflowY: "auto", border: "1px solid #dee2e6", borderRadius: "0.375rem" }}>
          <Table
            striped
            bordered
            hover
            responsive
            className="mb-0"
          >
            <thead className="table-light" style={{ position: "sticky", top: 0, zIndex: 10 }}>
              <tr>
                <th>Date/Time</th>
                <th>User</th>
                <th>Role</th>
                <th>Job Name</th>
                <th>Job Number</th>
                <th>Field Changed</th>
                <th>Previous Value</th>
                <th>New Value</th>
              </tr>
            </thead>
            <tbody>
              {filteredLog.map((item, index) => (
                <tr key={item.ID || index}>
                  <td style={{ whiteSpace: "nowrap" }}>
                    {moment(item.DateTime).format("MM/DD/YYYY hh:mm A")}
                  </td>
                  <td>{formatValue(item.CurrentUser)}</td>
                  <td>{formatValue(item.UserRole)}</td>
                  <td>{formatValue(item.JobName)}</td>
                  <td style={{ whiteSpace: "nowrap" }}>{formatValue(item.JobNumber)}</td>
                  <td>{formatFieldName(item.Field)}</td>
                  <td>{formatValue(item.PrevValue)}</td>
                  <td>{formatValue(item.NewValue)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default PanelSizingChangeLog;
